<template>
  <div :class="['card-profile-stats', { page }]">
    <div class="main">
      <h1 class="card-text">
        {{ card.year }} {{ card.set }} {{ card.player }}{{ card.number ? ` #${card.number}` : ''}}
      </h1>

      <card-item-chips
        profile
        :card="card"
        :is-public="isPublic"
      />
    </div>

    <section :class="['stats-grid', { page }]">
      <stat-item
        v-if="card.stats && card.stats.newestSale && card.stats.newestSale.price"
        label="Last Sold Price"
        :value="$filters.currency(card.stats.newestSale.price, { full: true })"
      />

      <stat-item
        v-if="card.stats && card.stats.newestSale && card.stats.newestSale.date"
        label="Last Sold Date"
        :value="$filters.date(card.stats.newestSale.date)"
      />

      <value-display
        v-if="!isPublic"
        subtle
        :confidence="isPrivateCard ? null : confidence"
        :value="currentValue"
        :estimated="isPrivateCard"
        :blur="!card.collectionCardId && confidence === 1"
      />

      <stat-item
        v-if="showPop"
        label="Pop"
        :value="card.pop ? $filters.number(card.pop) : 'N/A'"
      >
        <template #after>
          <icon-button
            v-if="!isPublic && card.profileId"
            class="pop-report"
            icon="menu_book"
            tooltip="View in depth population report"
            @click="showPopData"
          />
        </template>
      </stat-item>

      <stat-item
        v-if="!isPublic && gradeRatio"
        label="Grade Ratio Value"
        :value="$filters.currency(gradeRatio.ratio * gradeRatio.currentValue)"
      >
        <template #after>
          <span>
            <helper-icon :help-text="`Graded Ratio Value attempts to estimate the value of a card that hasn't sold in the last year using a recent comp from another grade of this same card. Using sales in the past where this card sold around the same time as the ${gradeRatio.condition} copy, the ratio was found to be ${gradeRatio.ratio}. Using the current value of the ${gradeRatio.condition} we have determined the value of this card.`" />
            <external-link href="https://cardladder.zendesk.com/hc/en-us/articles/13406278121751-Grade-Ratio-Value" />
          </span>
        </template>
      </stat-item>

      <stat-item
        v-if="card.cert && card.collectionCardId"
        label="Graded Cert #"
        :value="card.cert"
      >
        <template #after>
          <external-link
            v-if="certLink"
            :href="certLink"
            @click.native="$copyText(card.cert.toString())"
          />
        </template>
      </stat-item>

      <stat-item
        v-if="cardId && card.stats && card.stats.marketCap"
        label="Market Cap"
        :value="$filters.currency(card.stats.marketCap)"
      >
        <template #after>
          <helper-icon help-text="Market Cap is simply the current value multiplied by the population" />
        </template>
      </stat-item>

      <stat-item
        v-if="card.collectionCardId && card.datePurchased"
        label="Date Purchased"
        :value="$filters.date(card.datePurchased)"
      />

      <stat-item
        v-if="card.collectionCardId && card.sold && card.dateSold"
        label="Date Sold"
        :value="$filters.date(card.dateSold)"
      />

      <stat-item
        v-if="gapExists && !card.collectionCardId"
        :label="card.stats.gap > 0 ? 'Undervalued' : 'Overvalued'"
        :value="gapValue"
      >
        <pro-label
          v-if="isPublic"
          tooltip="Go Pro today to see if this card is due for a price jump"
          as="nuxt-link"
          to="/pricing"
        />

        <template #after>
          <helper-icon v-if="!isPublic" help-text="Undervalued/Overvalued is the percentage difference between the Last Sold Price and the CL Value algorithm." />
        </template>
      </stat-item>
    </section>

    <collection-card-badges @clickEstimation="handleClickEstimationBadge" :card="card" />
    <admin-card-badges :card="card" />
  </div>
</template>

<script>
import { sortOtherGrades } from '../utils/format.util';

import CardItemChips from './CardItemChips';
import CollectionCardBadges from './CollectionCardBadges';
import AdminCardBadges from './AdminCardBadges';
import HelperIcon from './HelperIcon';
import ProLabel from './ProLabel';
import StatItem from './StatItem';
import ValueDisplay from './ValueDisplay';
import ExternalLink from './ExternalLink';

export default {
  components: {
    CardItemChips,
    CollectionCardBadges,
    AdminCardBadges,
    HelperIcon,
    ProLabel,
    StatItem,
    ValueDisplay,
    ExternalLink,
  },

  props: {
    card: Object,
    cardId: String,
    isPublic: Boolean,
    isStaff: Boolean,
    page: Boolean,
  },

  computed: {
    isPrivateCard() {
      return this.card.collectionCardId && !this.card.cardId;
    },

    isSold() {
      return this.card.sold;
    },

    confidence() {
      return this.card.stats?.guideValueConfidence || 1;
    },

    currentValue() {
      let currentValue = this.card.currentValue || this.card.estimatedCurrentValue;

      if (typeof currentValue !== 'number') {
        return 'N/A';
      }

      return this.$filters.currency(currentValue);
    },

    gapExists() {
      return typeof this.card?.stats?.gap === 'number' && Math.abs(this.card?.stats?.gap) > 0.01;
    },

    gapValue() {
      if (this.isPublic) {
        return null;
      }

      return this.$filters.percentage(Math.abs(this.card?.stats?.gap || 0));
    },

    gradeRatio() {
      let gradeRatio = null;

      if (this.card.otherGrades && this.card.otherGrades.length > 0) {
        const sortedOtherGrades = sortOtherGrades(this.card.otherGrades);

        for (let i = 0; i < sortedOtherGrades.length; i += 1) {
          if (sortedOtherGrades[i].ratio && sortedOtherGrades[i].currentValue) {
            gradeRatio = sortedOtherGrades[i];
            break;
          }
        }
      }

      return gradeRatio;
    },

    showPop() {
      if (!this.isPrivateCard && (!this.card?.pop || !this.card?.grader)) {
        return false;
      }

      if (this.isPrivateCard && !this.card?.pop) {
        return false;
      }

      if (this.card?.popIgnore && !this.card?.pop && !this.isStaff) {
        return false;
      }

      return true;
    },

    certLink() {
      if (this.card?.grader === 'psa' && this.card.cert) {
        return `https://www.psacard.com/cert/${this.card.cert}/psa`;
      } else if (this.card?.grader === 'beckett' && this.card.cert) {
        return `https://www.beckett.com/grading/card-lookup?item_id=${this.card.cert}`;
      } else if (this.card?.grader === 'sgc') {
        return 'https://www.gosgc.com/cert-code-lookup';
      } else if (this.card?.grader === 'cgc') {
        return 'https://www.cgccards.com/CERTLOOKUP/card-grading/grading-scale/';
      }

      return null;
    },
  },

  methods: {
    showPopData() {
      this.$router.push(`/profiles/${this.card.profileId}`);
    },

    handleClickEstimationBadge() {
      this.$bus.$emit('collection-edit-card-value', {
        id: this.card.collectionCardId,
        collectionId: this.card.collectionId,
        display: 'valueIntro',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.card-profile-stats {
  --logo-height: 16px;
}

.main {
  margin-bottom: $gp;
}

.card-text {
  margin-bottom: $gp * .5;
  font-size: 22px;
  font-weight: bold;
}

.affiliate-text {
  margin-top: $gp * .5;
}

.stats-grid {
  display: grid;
  grid-gap: $gp $gp * .5;
  grid-template-columns: repeat(4, 1fr);

  @media($medium-down) {
    grid-template-columns: repeat(2, 1fr);
  }

  &.page {
    @media($xlarge-down) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media($large-down) {
      grid-template-columns: repeat(1, 1fr);
    }
  }
}

.pro-label,
.pop-report {
  margin-left: 5px;
}
</style>
